import WebApp from '@twa-dev/sdk';
import React, { Suspense, lazy, useEffect } from 'react';
import './App.css';
import './styles/components/index.css';

/**
 * Параллельная загрузка MUIThemeProvider + AppProvider в одном lazy-чанке.
 * Оба грузятся одновременно через Promise.all, затем оборачиваются в единый компонент.
 */
const CoreProviders = lazy(async () => {
    const [{ default: MUIThemeProvider }, { AppProvider }] = await Promise.all([
        import('./components/providers/MUIThemeProvider'),
        import('./contexts/AppContext'),
    ]);
    return {
        default: ({ children }: { children: React.ReactNode }) => (
            <MUIThemeProvider>
                <AppProvider>{children}</AppProvider>
            </MUIThemeProvider>
        ),
    };
});

/**
 * Все вторичные провайдеры + AppContent в одном чанке.
 */
const AppProviders = lazy(() => import('./components/AppProviders'));

/**
 * Предзагрузка модулей и инициализация Telegram WebApp.
 * Аналитика (PostHog, Telegram Analytics) грузится отложенно.
 */
const Preloader: React.FC = () => {
    useEffect(() => {
        try {
            WebApp.ready();

            const startParam = WebApp.initDataUnsafe?.start_param?.trim() || '';
            const isFullscreenMode =
                !startParam || startParam.startsWith('fs_');

            if (isFullscreenMode) {
                WebApp.expand();
                if (typeof WebApp.requestFullscreen === 'function') {
                    WebApp.requestFullscreen();
                }
            }

            if (typeof WebApp.disableVerticalSwipes === 'function') {
                WebApp.disableVerticalSwipes();
            }

            const setInitialSafeAreaTop = () => {
                const insets = WebApp.contentSafeAreaInset || WebApp.safeAreaInset;
                if (insets) {
                    const root = document.documentElement;
                    root.style.setProperty('--tg-content-safe-area-inset-top', `${insets.top}px`);
                    root.style.setProperty('--tg-content-safe-area-inset-left', `${insets.left}px`);
                    root.style.setProperty('--tg-content-safe-area-inset-right', `${insets.right}px`);
                }

                const systemInsets = WebApp.safeAreaInset;
                if (systemInsets) {
                    const root = document.documentElement;
                    root.style.setProperty('--tg-safe-area-inset-top', `${systemInsets.top}px`);
                    root.style.setProperty('--tg-safe-area-inset-left', `${systemInsets.left}px`);
                    root.style.setProperty('--tg-safe-area-inset-right', `${systemInsets.right}px`);
                }
            };

            const setSafeAreaBottom = () => {
                const insets = WebApp.contentSafeAreaInset || WebApp.safeAreaInset;
                if (insets) {
                    document.documentElement.style.setProperty('--tg-content-safe-area-inset-bottom', `${insets.bottom}px`);
                }

                const systemInsets = WebApp.safeAreaInset;
                if (systemInsets) {
                    document.documentElement.style.setProperty('--tg-safe-area-inset-bottom', `${systemInsets.bottom}px`);
                }
            };

            const setInitialViewportStableHeight = () => {
                const viewportStableHeight = WebApp.viewportStableHeight;
                if (viewportStableHeight) {
                    document.documentElement.style.setProperty('--tg-viewport-stable-height', `${viewportStableHeight}px`);
                }
            };

            setInitialSafeAreaTop();
            setSafeAreaBottom();
            setInitialViewportStableHeight();

            (WebApp as any).onEvent('contentSafeAreaInsetChanged', setSafeAreaBottom);
            (WebApp as any).onEvent('safeAreaChanged', setSafeAreaBottom);

        } catch (e) {
            console.error('Error initializing Telegram Web App:', e);
        }

        // Параллельная предзагрузка ВСЕХ чанков — устраняет водопад
        Promise.all([
            import('./components/providers/MUIThemeProvider'),
            import('./contexts/AppContext'),
            import('./components/AppProviders'),
            import('./pages/HomePage'),
        ]).catch(() => {});

        // Отложенная инициализация аналитики — не блокирует рендер
        const initAnalytics = async () => {
            // В обычном браузере @telegram-apps/analytics бросает LaunchParamsRetrieveError.
            // Аналитику включаем только в реальном Telegram WebApp.
            const tg = (window as any)?.Telegram?.WebApp;
            const initData: string | undefined = tg?.initData;
            const isRealTelegram =
                !!tg &&
                typeof initData === 'string' &&
                initData.length > 0 &&
                (initData.includes('auth_date=') ||
                    initData.includes('user=') ||
                    initData.includes('query_id='));

            if (!isRealTelegram) {
                return;
            }

            try {
                const [{ initPostHog }, telegramAnalyticsModule] = await Promise.all([
                    import('./analytics/posthog'),
                    import('@telegram-apps/analytics'),
                ]);

                initPostHog();

                const analyticsToken = process.env.REACT_APP_TG_ANALYTICS_TOKEN;
                const analyticsAppName = process.env.REACT_APP_TG_ANALYTICS_APP_NAME;
                if (analyticsToken && analyticsAppName) {
                    telegramAnalyticsModule.default.init({
                        token: analyticsToken,
                        appName: analyticsAppName,
                    });
                }
            } catch {
                // Аналитика не критична
            }
        };

        if ('requestIdleCallback' in window) {
            (window as any).requestIdleCallback(initAnalytics);
        } else {
            setTimeout(initAnalytics, 100);
        }
    }, []);
    return null;
};

const App: React.FC = () => {
    return (
        <>
            <Preloader />

            <Suspense fallback={null}>
                <CoreProviders>
                    <Suspense fallback={null}>
                        <AppProviders />
                    </Suspense>
                </CoreProviders>
            </Suspense>
        </>
    );
};

export default App;
